<template>
    <main>
        <component
            :is="page.widgetsData[id].component"
            v-for="id in page.sections.main.widgets"
            :key="id"
            :data="page.widgetsData[id]"
            :variant="$options.SectionHeaderVariant.DailyChat"
            :content-size="$options.ContentSize.XLarge"
            @loadMore="onLoadMore"
            @openConvo="onOpenConvo"
        />
    </main>
</template>

<script>
import WidgetContainer from '@/components/WidgetContainer';
import ContentService from '@/services/content.service';
import { ErrorCodes, StorySlug, ContentSize, SectionHeaderVariant } from '@/enums';
import { mapState, mapActions } from 'vuex';
import { scrollToAsync } from '@/services/Utils';
import RouterService from '@/services/router.service';
import AbTestService from '@/services/ab-test.service';

export default {
    name: 'Daily',
    layout: 'daily-chat/product',
    components: {
        WidgetContainer,
    },
    async asyncData(context) {
        try {
            const abTestService = new AbTestService(context.app.$cookies, context.route);
            const contentService = new ContentService(
                context.app.$storyapi,
                abTestService,
                context.$logger,
                context.app.i18n.locale,
            );
            const storySlug = StorySlug.DailyChat;
            const sections = ['main'];

            const story = await contentService.getStory({
                story: storySlug,
                version: context.app.$storyblokHelper.version,
                locale: context.app.i18n.locale,
                hasFallback: true,
            });

            const page = await contentService.getPageContent(story, sections);
            abTestService.setCookie();

            if (page.errors.length) {
                page.errors.map((item) =>
                    context.$logger.error({
                        msg: 'error.getPageContent Daily',
                        item,
                        errorCode: ErrorCodes.FailedToGetPageContent,
                    }),
                );
                context.$notifications.error(context.app.i18n.t('notification.pageLoadFailed'), { dismissable: true });
            }

            const layout = story.content.component || '';

            return {
                page,
                layout,
            };
        } catch (error) {
            context.$logger.error({ msg: 'error.convosContent', error });
            context.$notifications.error(context.app.i18n.t('notification.pageLoadFailed'), { dismissable: true });
        }
    },
    data() {
        return {
            page: { widgetsData: {}, sections: { main: { widgets: [] } } },
        };
    },
    computed: {
        ...mapState('auth', ['isLoggedIn', 'authRefreshed']),
        ...mapState('channels', ['activeChannel']),
    },
    watch: {
        authRefreshed: {
            deep: true,
            immediate: true,
            handler(value) {
                if (value && this.isLoggedIn) {
                    window.location.href = RouterService.getRedirectPath(this.$route, '/');
                }
            },
        },
    },
    created() {
        this.$options.ContentSize = ContentSize;
        this.$options.SectionHeaderVariant = SectionHeaderVariant;
    },
    async mounted() {
        await this.refreshAuth();
        this.setClient();
        this.abTestService = new AbTestService(this.$cookies, this.$route);
        this.contentService = new ContentService(this.$storyapi, this.abTestService);

        this.$storyblokHelper.addEventListener('input', this.onStoryblokInput);
        this.$storyblokHelper.addEventListener('enterComponent', this.onStoryblokEnterComponent);
    },
    beforeDestroy() {
        this.$storyblokHelper.removeEventListener('input', this.onStoryblokInput);
    },
    methods: {
        ...mapActions('auth', ['refreshAuth', 'setClient']),
        ...mapActions('channels', ['getChannel']),
        ...mapActions('chatController', ['startChat']),
        async onStoryblokInput(event) {
            if (event.story.id === this.page.id) {
                this.page = await this.contentService.getPageContent(event.story, ['main']);
            }
        },
        onStoryblokEnterComponent(event) {
            const selectedVariant = this.contentService.getSelectedAbTestVariant(
                this.page.widgetsData,
                event.componentId,
            );

            if (selectedVariant) {
                this.contentService.replaceContentWithAbTestVariant(this.page.widgetsData, selectedVariant);
            }
        },
        async onOpenConvo(convo) {
            if (this.isLoggedIn) {
                await this.getChannel(convo.channel.slug);
                this.startChat(this.activeChannel);
                window.location.href = '/';
            } else {
                await scrollToAsync({ top: 0, left: 0, behavior: 'smooth' });
                this.$nuxt.$emit('dailyChatSubscriberFocus');
            }
        },
        onLoadMore() {
            window.location.href = '/';
        },
    },
};
</script>
